import { actions, response } from 'actions';
import { consoleLogger } from 'helpers';
import { Auth } from 'aws-amplify';
import axios from 'axios';
import clientConfig from 'clientConfig';

const convertData = (item) => ({
  name: item.account.Attributes.name,
  preferred_username: item.account.Attributes.preferred_username,
  email: item.account.Attributes.email,
  phone_number: item.account.Attributes.phone_number,
  sub: item.account.Username,
  enabled: item.account.Enabled,
  user_status: item.account.UserStatus,
  created_at: item.account.UserCreateDate,
  updated_at: item.account.UserLastModifiedDate
});

/**
 * 管理者アカウント一覧を取得する
 * @param  {object} params
 * @return {object} 処理結果 
 */
export const getAccounts = (params, cancelToken, loadMore) => async dispatch => {
  const currentSession = await Auth.currentSession();
  const url = clientConfig['api_url'] + '/adm/accounts/';
  const requestData = {
    method: 'get',
    url: url,
    headers: { 'Authorization': currentSession.idToken.jwtToken },
    params: params,
    cancelToken: cancelToken === null ? null : cancelToken.token,
  };
  const result = {...response};
  return new Promise((resolve) => {
    axios(requestData)
      .then(res => {
        consoleLogger('API GET:' + url);
        consoleLogger(res);
        if (res.status === 200) {
          const items = [];
          for (const key in res.data.items) {
            items.push(convertData(res.data.items[key]));
          }
          result.count = res.data.items.length;
          dispatch({
            type: actions.FETCH_ACCOUNTS,
            search_attr: params.search_attr,  // 検索項目
            search_str: params.search_str,    // 検索ワード
            pagination: res.data.pagination !== undefined ? res.data.pagination['PaginationToken'] : null,  // ページネーショントークン
            load_more: loadMore,
            items: items,
          });
        } else {
          result.errcode = res.data.errcode;
        }
        resolve(result);
      }).catch(err => {
        consoleLogger(err);
        if (axios.isCancel(err)) {
          return false;
        } else {
          if (err.response !== undefined) {
            result.status = err.response.status;
            result.errcode = err.response.data.errcode;  
          } else {
            result.status = 500;
            result.errcode = err.message;  
          }
          resolve(result);      
        }      
      });
  });
};

/**
 * 指定した管理者アカウントを取得する
 * @param  {string} ユーザーID
 * @return {object} 処理結果 
 */
export const getAccount = (sub, cancelToken) => async dispatch => {
  const currentSession = await Auth.currentSession();
  const url = clientConfig['api_url'] + '/adm/accounts/' + sub;
  const requestData = {
    method: 'get',
    url: url,
    headers: { 'Authorization': currentSession.idToken.jwtToken },
    cancelToken: cancelToken.token
  };
  const result = {...response};
  return new Promise((resolve) => {
    axios(requestData)
      .then(res => {
        consoleLogger('API GET:' + url);
        consoleLogger(res);
        if (res.status === 200) {
          const item = convertData(res.data.item);
          result.count = 1;
          result.data = item;
          dispatch({
            type: actions.FETCH_ACCOUNT,
            sub: sub,
            item: result.data,
          });
        } else {
          result.errcode = res.data.errcode;
        }
        resolve(result);
      }).catch(err => {
        consoleLogger(err);
        if (axios.isCancel(err)) {
          return false;
        } else {
          if (err.response !== undefined) {
            result.status = err.response.status;
            result.errcode = err.response.data.errcode;  
          } else {
            result.status = 500;
            result.errcode = err.message;  
          }
          resolve(result);      
        }       
      });
  });
};

/**
 * 指定した管理者アカウントを削除する
 * @param  {string} sub  ユーザーID
 * @return {object} 処理結果
 */
export const deleteAccount = (sub) => async dispatch => {
  const currentSession = await Auth.currentSession();
  const url = clientConfig['api_url'] + '/adm/accounts/' + sub;
  const requestData = {
    method: 'delete',
    url: url,
    headers: { 'Authorization': currentSession.idToken.jwtToken },
  };
  const result = {...response};
  return new Promise((resolve) => {
    axios(requestData)
      .then(res => {
        consoleLogger('API DELETE:' + url);
        consoleLogger(res);
        if (res.status === 200) {
          dispatch({ 
            type: actions.DELETE_ACCOUNT,
            sub: sub,
          });
        } else {
          result.errcode = res.data.errcode;
        }
        resolve(result);
      }).catch(err => {
        consoleLogger(err.response);
        if (err.response !== undefined) {
          result.status = err.response.status;
          result.errcode = err.response.data.errcode;  
        } else {
          result.status = 500;
          result.errcode = err.message;  
        }
        resolve(result);      
      });
  });
};

/**
 * 管理者アカウントの有効・無効を切り替える
 * @param  {string} sub      ユーザーID
 * @param  {bool}   enabled  公開/非公開フラグ
 * @return {object} 処理結果
 */
export const toggleAccountEnableDisable = (sub, enabled) => async dispatch => {
  const currentSession = await Auth.currentSession();
  const url = clientConfig['api_url'] + '/adm/accounts/' + sub + '/status';
  const requestData = {
    method: 'put',
    url: url,
    headers: { 
      'Authorization': currentSession.idToken.jwtToken,
      'Content-Type': 'application/json'
    },
    params: {
      enabled: enabled ? '1' : '0'
    }
  };
  const result = {...response};
  return new Promise((resolve) => {
    axios(requestData)
      .then(res => {
        consoleLogger('API PUT:' + url);
        consoleLogger(res);
        if (res.status === 200) {
          dispatch({ 
            type: actions.TOGGLE_ACCOUNT_ENABLE_DISABLE,
            sub: sub,
            enabled: enabled
          });
        } else {
          result.errcode = res.data.errcode;
        }
        resolve(result);
      }).catch(err => {
        consoleLogger(err.response);
        if (err.response !== undefined) {
          result.status = err.response.status;
          result.errcode = err.response.data.errcode;  
        } else {
          result.status = 500;
          result.errcode = err.message;  
        }
        resolve(result);      
      });
  });
};

/**
 * 管理者アカウントの更新
 * @param  {string} sub ユーザーID
 * @param  {object} params
 * @return {object} 処理結果
 */
export const updateAccount = (sub, params) => async dispatch => {
  const currentSession = await Auth.currentSession();
  const url = clientConfig['api_url'] + '/adm/accounts/' + sub;
  const requestData = {
    method: 'put',
    url: url,
    headers: { 'Authorization': currentSession.idToken.jwtToken },
    params: params
  };
  const result = {...response};
  return new Promise((resolve) => {
    axios(requestData)
      .then(res => {
        consoleLogger('API PUT:' + url);
        consoleLogger(res);
        if (res.status === 200) {
          dispatch({ type: actions.UPDATE_ACCOUNT });
        } else {
          result.errcode = res.data.errcode;
        }
        resolve(result);
      }).catch(err => {
        consoleLogger(err.response);
        if (err.response !== undefined) {
          result.status = err.response.status;
          result.errcode = err.response.data.errcode;  
        } else {
          result.status = 500;
          result.errcode = err.message;  
        }
        resolve(result);       
      });
  });
};

/**
 * 管理者アカウントの登録
 * @param  {object} params
 * @return {object} 処理結果
 */
export const registAccount = (params) => async dispatch => {
  const currentSession = await Auth.currentSession();
  const url = clientConfig['api_url'] + '/adm/accounts/';
  const requestData = {
    method: 'post',
    url: url,
    headers: { 'Authorization': currentSession.idToken.jwtToken },
    params: params,
  };
  const result = {...response};
  return new Promise((resolve) => {
    axios(requestData)
      .then(res => {
        consoleLogger('API POST:' + url);
        consoleLogger(res);
        if (res.status === 200) {
          dispatch({ type: actions.REGIST_ACCOUNT });
        } else {
          result.errcode = res.data.errcode;
        }
        resolve(result);
      }).catch(err => {
        consoleLogger(err.response);
        if (err.response !== undefined) {
          result.status = err.response.status;
          result.errcode = err.response.data.errcode;  
        } else {
          result.status = 500;
          result.errcode = err.message;  
        }
        resolve(result);   
      });
  });
};